if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbs: this.querySelector('.flickity-gallery__thumbs-scroll'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isFlickityMode = this.dataset.desktopLayout === 'flickity';

        if (this.isFlickityMode) {
          this.initFlickity();
          return;
        }

        if (!this.elements.thumbs) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbs.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbs.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.isFlickityMode) {
          this.flickitySetActiveMedia(mediaId);
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbs) {
            const activeThumbnail = this.elements.thumbs.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbs) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbs) return;
        const activeThumbnail = this.elements.thumbs.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbs || !thumbnail) return;

        this.elements.thumbs
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbs.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbs.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      async initFlickity() {
        // Wait for Flickity to be available
        while (typeof window.Flickity === 'undefined') {
          await new Promise((r) => setTimeout(r, 50));
        }

        // Abort previous listeners to avoid duplicate bindings on re-init
        if (this._flickityAbort) this._flickityAbort.abort();
        this._flickityAbort = new AbortController();
        const signal = this._flickityAbort.signal;

        const mainEl = this.elements.viewer;
        const thumbsEl = this.elements.thumbs;

        // Navigation buttons
        const prevBtn = this.querySelector('.flickity-gallery__btn-prev');
        const nextBtn = this.querySelector('.flickity-gallery__btn-next');

        // Init main Flickity carousel
        this.mainFlickity = new Flickity(mainEl, {
          cellSelector: '.flickity-gallery__cell',
          contain: true,
          pageDots: false,
          prevNextButtons: false,
          draggable: true,
          autoPlay: false,
          wrapAround: false,
          adaptiveHeight: false,
        });

        // Navigation buttons for main carousel
        if (prevBtn) {
          prevBtn.addEventListener('click', () => this.mainFlickity.previous(), { signal });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', () => this.mainFlickity.next(), { signal });
        }

        // Mobile scrollbar indicator
        this.scrollbarThumb = this.querySelector('.flickity-gallery__scrollbar-thumb');
        if (this.scrollbarThumb) {
          const cellCount = this.mainFlickity.cells.length;
          if (cellCount > 1) {
            const thumbWidth = 100 / cellCount;
            this.scrollbarThumb.style.width = thumbWidth + '%';
            this.scrollbarThumb.style.left = '0%';
            this.mainFlickity.on('change', (index) => {
              this.scrollbarThumb.style.left = (index * thumbWidth) + '%';
            });
          } else {
            this.scrollbarThumb.parentElement.parentElement.style.display = 'none';
          }
        }

        // Thumbnail click + sync
        if (thumbsEl) {
          const thumbCells = thumbsEl.querySelectorAll('.flickity-gallery__thumb-cell');
          thumbCells.forEach((thumb, index) => {
            thumb.addEventListener('click', () => {
              this.mainFlickity.select(index);
            }, { signal });
          });

          // Sync thumbnails on main carousel change
          this.mainFlickity.on('change', (index) => {
            this.syncThumbnail(index);
            // Play media if needed
            const cells = mainEl.querySelectorAll('.flickity-gallery__cell');
            if (cells[index]) {
              this.playActiveMedia(cells[index]);
            }
          });
        } else {
          // No thumbs, still handle media play on change
          this.mainFlickity.on('change', (index) => {
            const cells = mainEl.querySelectorAll('.flickity-gallery__cell');
            if (cells[index]) {
              this.playActiveMedia(cells[index]);
            }
          });
        }
      }

      syncThumbnail(activeIndex) {
        const thumbsEl = this.elements.thumbs;
        if (!thumbsEl) return;

        const thumbCells = thumbsEl.querySelectorAll('.flickity-gallery__thumb-cell');
        thumbCells.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === activeIndex);
        });

        // Scroll active thumbnail to vertical center of the container
        if (thumbCells[activeIndex]) {
          const thumb = thumbCells[activeIndex];
          const container = thumbsEl;
          const targetScrollTop = thumb.offsetTop - container.offsetTop - (container.clientHeight - thumb.offsetHeight) / 2;
          container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        }
      }

      flickitySetActiveMedia(mediaId) {
        if (!this.mainFlickity) return;
        const cells = this.elements.viewer.querySelectorAll('.flickity-gallery__cell[data-media-id]');
        let targetIndex = 0;
        cells.forEach((cell, index) => {
          if (cell.dataset.mediaId === mediaId) {
            targetIndex = index;
          }
        });
        this.mainFlickity.select(targetIndex);
        this.syncThumbnail(targetIndex);
      }
    }
  );
}
